export const SLOT_COUNT = 5;
export const PUZZLE_SHAPE = [7, 5, 4, 4];
export const SCORING_VERSION = "wb-score-v2";
export const CLEAN_BOARD_BONUS = 15;
export const DAILY_TIME_ZONE = "Europe/London";

export const DIFFICULTIES = {
  easy: { label: "Easy", preview: 4, moves: 3 },
  medium: { label: "Medium", preview: 2, moves: 2 },
  hard: { label: "Hard", preview: 0, moves: 1 }
};

export function scoreForLength(length) {
  if (!Number.isInteger(length) || length < 2) return 0;
  const cappedLength = Math.min(length, 12);
  return cappedLength * (cappedLength - 1);
}

export function normalizeWord(word, locale = "en-GB") {
  if (locale === "en-GB" || locale === "en-US") {
    return String(word || "").trim().toLowerCase();
  }

  return String(word || "").normalize("NFKC").trim().toLowerCase();
}

export function isSupportedLocale(locale) {
  return locale === "en-GB" || locale === "en-US";
}

export function isValidDateKey(date) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(date || ""));
}

export function dateKeyForTimeZone(now = new Date(), timeZone = DAILY_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function isFutureDate(date, now = new Date()) {
  if (!isValidDateKey(date)) return true;
  const today = dateKeyForTimeZone(now);
  return date > today;
}

export function isPlayableGameDate(date, now = new Date()) {
  return isValidDateKey(date) && date === dateKeyForTimeZone(now);
}

export function analyseRows(rows, dictionaryWords, locale = "en-GB") {
  const validWords = dictionaryWords instanceof Set ? dictionaryWords : new Set(dictionaryWords || []);

  return rows.map((row) => {
    const letters = Array.isArray(row) ? row : [];
    const word = normalizeWord(letters.join(""), locale);
    const valid = word.length > 0 && validWords.has(word);

    return {
      letters,
      word,
      valid,
      score: valid ? scoreForLength(letters.length) : 0
    };
  });
}

export function hasCleanBoardBonus(analyses, expectedLetterCount) {
  const usedRows = analyses.filter((item) => item.letters.length > 0);
  const usedLetters = analyses.reduce((sum, item) => sum + item.letters.length, 0);
  return usedLetters === expectedLetterCount && usedRows.length > 0 && usedRows.every((item) => item.valid);
}

export function totalScore(analyses, expectedLetterCount) {
  const base = analyses.reduce((sum, item) => sum + item.score, 0);
  return base + (hasCleanBoardBonus(analyses, expectedLetterCount) ? CLEAN_BOARD_BONUS : 0);
}

export function challengeLetterCount(challenge) {
  return (challenge?.letters || []).length;
}

export function validateDictionaryManifest(dictionary) {
  const errors = [];
  if (!dictionary || typeof dictionary !== "object") return ["Dictionary must be an object."];
  if (dictionary.schemaVersion !== 1) errors.push("Dictionary schemaVersion must be 1.");
  if (!isSupportedLocale(dictionary.locale)) errors.push(`Unsupported dictionary locale: ${dictionary.locale || "(missing)"}.`);
  if (!dictionary.version) errors.push("Dictionary version is required.");

  const generationWords = dictionary.generationWords;
  const playWords = dictionary.playWords;
  validateWordList("generationWords", generationWords, errors);
  validateWordList("playWords", playWords, errors);

  const playable = new Set(playWords || []);
  for (const word of generationWords || []) {
    if (!playable.has(word)) errors.push(`Generation word ${word} is not playable.`);
  }

  if (dictionary.counts?.generation !== generationWords?.length) {
    errors.push("Dictionary generation count is incorrect.");
  }
  if (dictionary.counts?.play !== playWords?.length) {
    errors.push("Dictionary play count is incorrect.");
  }
  return errors;
}

function validateWordList(name, words, errors) {
  if (!Array.isArray(words) || words.length === 0) {
    errors.push(`${name} must be a non-empty array.`);
    return;
  }

  let previous = "";
  for (const word of words) {
    if (!/^[a-z]{2,15}$/.test(word)) errors.push(`${name} contains invalid word ${word}.`);
    if (word <= previous) errors.push(`${name} must be sorted with no duplicates near ${word}.`);
    previous = word;
  }
}

export function validatePuzzleManifest(manifest, dictionaryWords, now = new Date()) {
  const errors = [];
  const dictionary = dictionaryWords instanceof Set ? dictionaryWords : new Set(dictionaryWords || []);

  if (!manifest || typeof manifest !== "object") {
    return ["Manifest must be an object."];
  }

  if (manifest.schemaVersion !== 2) {
    errors.push("schemaVersion must be 2.");
  }

  if (!isSupportedLocale(manifest.locale)) {
    errors.push(`Unsupported locale: ${manifest.locale || "(missing)"}.`);
  }

  if (!isValidDateKey(manifest.date)) {
    errors.push(`Invalid date: ${manifest.date || "(missing)"}.`);
  } else if (isFutureDate(manifest.date, now)) {
    errors.push(`Manifest date ${manifest.date} is in the future.`);
  }

  if (manifest.publishStatus !== "published") {
    errors.push("publishStatus must be published.");
  }

  if (!manifest.puzzleVersion) {
    errors.push("puzzleVersion is required.");
  }

  for (const difficultyKey of Object.keys(DIFFICULTIES)) {
    const challenge = manifest.challenges?.[difficultyKey];
    if (!challenge) {
      errors.push(`Missing ${difficultyKey} challenge.`);
      continue;
    }

    if (challenge.difficulty !== difficultyKey) {
      errors.push(`${difficultyKey} challenge has mismatched difficulty.`);
    }

    if (!["wb-score-v1", SCORING_VERSION].includes(challenge.scoringVersion)) {
      errors.push(`${difficultyKey} has unsupported scoringVersion.`);
    }

    const config = challenge.config || {};
    const difficulty = DIFFICULTIES[difficultyKey];
    if (config.preview !== difficulty.preview) {
      errors.push(`${difficultyKey} preview must be ${difficulty.preview}.`);
    }
    if (config.rowSwaps !== difficulty.moves) {
      errors.push(`${difficultyKey} rowSwaps must be ${difficulty.moves}.`);
    }

    const poolWords = challenge.poolWords || [];
    if (!Array.isArray(poolWords) || poolWords.length !== PUZZLE_SHAPE.length) {
      errors.push(`${difficultyKey} poolWords must have ${PUZZLE_SHAPE.length} words.`);
      continue;
    }

    const normalizedPoolWords = poolWords.map((word) => normalizeWord(word, manifest.locale));
    const poolWordSet = new Set(normalizedPoolWords);
    if (poolWordSet.size !== normalizedPoolWords.length) {
      errors.push(`${difficultyKey} poolWords contain duplicates.`);
    }

    normalizedPoolWords.forEach((word, index) => {
      if (word.length !== PUZZLE_SHAPE[index]) {
        errors.push(`${difficultyKey} pool word ${word} must be ${PUZZLE_SHAPE[index]} letters.`);
      }
      if (!dictionary.has(word)) {
        errors.push(`${difficultyKey} pool word ${word} is missing from dictionary.`);
      }
    });

    const poolLetters = normalizedPoolWords.join("").split("").sort().join("");
    const letters = challenge.letters || [];
    if (!Array.isArray(letters) || letters.length !== poolLetters.length) {
      errors.push(`${difficultyKey} letters must contain ${poolLetters.length} entries.`);
    } else {
      const letterKey = letters.map((letter) => normalizeWord(letter, manifest.locale)).sort().join("");
      if (letterKey !== poolLetters) {
        errors.push(`${difficultyKey} letters do not match pool words.`);
      }
    }
  }

  return errors;
}

export function validateScoreSubmission({ payload, manifest, dictionaryWords }) {
  const errors = [];
  const challenge = manifest?.challenges?.[payload?.difficulty];
  const difficulty = DIFFICULTIES[payload?.difficulty];

  if (!challenge || !difficulty) {
    errors.push("Unknown difficulty.");
    return { ok: false, errors };
  }

  if (payload.locale !== manifest.locale) errors.push("Locale does not match puzzle.");
  if (payload.date !== manifest.date) errors.push("Date does not match puzzle.");
  if (payload.puzzleVersion !== manifest.puzzleVersion) errors.push("Puzzle version does not match.");

  const rows = Array.isArray(payload.finalRows) ? payload.finalRows : [];
  if (rows.length !== SLOT_COUNT) {
    errors.push(`finalRows must contain ${SLOT_COUNT} rows.`);
  }

  const flatLetters = rows.flat().map((letter) => normalizeWord(letter, manifest.locale));
  const expectedLetters = challenge.letters.map((letter) => normalizeWord(letter, manifest.locale));
  if (flatLetters.slice().sort().join("") !== expectedLetters.slice().sort().join("")) {
    errors.push("Submitted rows do not use exactly the puzzle letters.");
  }

  if (!Number.isInteger(payload.rowSwapsUsed) || payload.rowSwapsUsed < 0 || payload.rowSwapsUsed > difficulty.moves) {
    errors.push("Invalid rowSwapsUsed.");
  }

  const rowSwapsLeft = difficulty.moves - Number(payload.rowSwapsUsed || 0);
  if (payload.rowSwapsLeft !== rowSwapsLeft) {
    errors.push("rowSwapsLeft does not match difficulty budget.");
  }

  const analyses = analyseRows(rows, dictionaryWords, manifest.locale);
  const computedScore = totalScore(analyses, expectedLetters.length);
  if (payload.claimedScore !== computedScore) {
    errors.push(`Claimed score ${payload.claimedScore} does not match computed score ${computedScore}.`);
  }

  return {
    ok: errors.length === 0,
    errors,
    computedScore,
    analyses
  };
}
